import { err, ok } from '@src/services/common/Result';
import { analytics } from '@src/utility/analytics';
import { LogManager } from '@src/utility/Logger';

import type { Result } from '@src/services/common/Result';
// Narrow interface to decouple from full MobX store surface.
export interface VideoCreatorStoreLike {
  isSaving: boolean;
  isBackgroundSaving: boolean;
  story?: { id?: string; title?: string };
  currentVideo?: { id?: string; title?: string };
  renderingStatus: string;
  renderQueueing: boolean;
  saveStoryAndVideo: (
    asFinal?: boolean,
    withRenderer?: boolean,
    resetTimeline?: boolean,
  ) => Promise<void>;
  setShowRepublishOnSaveModal: (value: boolean) => void;
}

export interface SaveStoryAndVideoServiceDeps {
  videoCreator: VideoCreatorStoreLike; // narrowed store contract
}

export interface SaveStoryAndVideoResult {
  republishRequired: boolean;
}

/**
 * Centralizes save logic for VideoCreator (button + keyboard shortcut).
 * Decides whether republish modal should be shown, triggers analytics, and invokes store save.
 */
export class SaveStoryAndVideoService {
  private readonly _vc: VideoCreatorStoreLike;
  private readonly _logger = LogManager.getLogger(
    'service:SaveStoryAndVideoService',
  );

  constructor(deps: SaveStoryAndVideoServiceDeps) {
    this._vc = deps.videoCreator;
  }

  async save(asFinal: boolean): Promise<Result<SaveStoryAndVideoResult>> {
    try {
      if (this._vc.isSaving || this._vc.isBackgroundSaving)
        return ok({ republishRequired: false });

      analytics.track('save_video', {
        storyId: this._vc.story?.id,
        storyTitle: this._vc.story?.title,
        videoId: this._vc.currentVideo?.id,
        videoTitle: this._vc.currentVideo?.title,
        asFinal,
      });

      const resetTimeline = false;
      const withRenderer = true;
      const republishRequired =
        this._vc.renderingStatus === 'rendering' || this._vc.renderQueueing;

      await this._vc.saveStoryAndVideo(asFinal, withRenderer, resetTimeline);

      if (republishRequired) {
        // Use explicit setter instead of direct property mutation
        this._vc.setShowRepublishOnSaveModal(true);
      }

      return ok({ republishRequired });
    } catch (e) {
      this._logger.error('Save failed', { e });
      return err(
        'UNKNOWN',
        'Failed to save story and video',
        e && typeof e === 'object' ? e : String(e),
      );
    }
  }
}

export default SaveStoryAndVideoService;

// Helper to construct the narrowed adapter from a larger store object without
// coupling call sites to the full surface. Kept in service module so consumers
// stay aligned with the minimal contract.
type AdapterSource = {
  isSaving: boolean;
  isBackgroundSaving: boolean;
  story?: { id?: string; title?: string };
  currentVideo?: { id?: string; title?: string };
  renderingStatus?: string; // optional on IVideoCreatorStore interface
  renderQueueing?: boolean; // optional on interface
  saveStoryAndVideo: (
    asFinal?: boolean,
    withRenderer?: boolean,
    resetTimeline?: boolean,
  ) => Promise<void>;
  setShowRepublishOnSaveModal: (v: boolean) => void;
};

export function createVideoCreatorSaveAdapter(
  videoCreator: AdapterSource,
): VideoCreatorStoreLike {
  // Return an object with dynamic getters so MobX reads live observable values
  // rather than a frozen snapshot. Methods are passed through directly.
  const adapter: VideoCreatorStoreLike = {
    get isSaving() {
      return videoCreator.isSaving;
    },
    get isBackgroundSaving() {
      return videoCreator.isBackgroundSaving;
    },
    get story() {
      const s = videoCreator.story;
      return s ? { id: s.id, title: s.title } : undefined;
    },
    get currentVideo() {
      const v = videoCreator.currentVideo;
      return v ? { id: v.id, title: v.title } : undefined;
    },
    get renderingStatus() {
      return videoCreator.renderingStatus || '';
    },
    get renderQueueing() {
      return !!videoCreator.renderQueueing;
    },
    saveStoryAndVideo: (
      asFinal?: boolean,
      withRenderer?: boolean,
      resetTimeline?: boolean,
    ) => videoCreator.saveStoryAndVideo(asFinal, withRenderer, resetTimeline),
    setShowRepublishOnSaveModal: (v: boolean) =>
      videoCreator.setShowRepublishOnSaveModal(v),
  };

  return adapter;
}
